import { useEffect, useState } from 'react';
import Moment from 'react-moment';
import { ReactComponent as CopyIcon } from '../../../assets/icons/copy.svg';
import { useHistory } from 'react-router-dom';
import Loading from '../../common/loading';
import Table from '../../common/table';
import { Urls } from '../../../config/urls';
import l from '../../../lang';
import '../transactions/styles.scss';
import './styles.scss';
import { createDate, getMappedCountryCode } from '../../../utils/index';
import { Routes } from '../../../config/routes';
import TopDashboardSection from '../../common/top-dashboard-section';
import ButtonMore from '../../common/button-more';
import TransactionCompletedStatusIcon from '../../common/transaction-completed-status-icon';
import { PaymentStatus } from '../../../constants';
import Card from '../../common/card';
import CardDescription from '../../common/card-description';
import { Empty, EmptyNoFound } from './empty';
import RefundsTransactionFilter from './filter';
import { useMediaQuery } from '@mui/material';
import ExportRefunds from './export';
import { useRefunds } from '../../../store/refunds';
import { Filters } from '../../../store/interfaces';
import AlertMessages from '../alert-messages';
import Tooltip from '@mui/material/Tooltip';
import StatusIcon from '../../common/status-icon';
import CopyToClipboard from 'react-copy-to-clipboard';

const Refunds = () => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const { loading, error, allRefunds, getRefunds } = useRefunds();

  const history = useHistory();
  const [wasCopied, setWasCopied] = useState(false);
  const initFilters = { search_by: '', status: '', country: '', from: '', to: '', paymentMethodId: '' };
  const [filter, setFilter] = useState<Filters>(initFilters);

  useEffect(() => {
    getRefunds(0, filter);
  }, [filter]);

  const hasFilter = Object.values(filter).some(value => value !== '');
  const hasRefunds = allRefunds && allRefunds?.data?.length > 0;

  const isRefundCompleted = (status?: string) => status === PaymentStatus.COMPLETED;

  const EmptyBody = () => {
    if (!error && !hasRefunds && !hasFilter) {
      return <Empty />;
    }
    if (error || !hasRefunds) {
      return <EmptyNoFound />;
    }
    return null;
  };

  return (
    <div className="transactions">
      <TopDashboardSection title={l('menu.item.REFUNDS_TRANSACTION')} subtitle={l('refunds.list.subheadline')} />
      <AlertMessages />
      <Card>
        <CardDescription>
          <div className="transactions_header">
            <span>{l('menu.item.REFUNDS_TRANSACTION.desc')}</span>
            <div className={isDesktop ? '' : 'transactions__export__btn'}>{<ExportRefunds />}</div>
          </div>
        </CardDescription>
        <div className="transactions__content">
          <div className="transactions__filter_wrapper">
            <RefundsTransactionFilter
              loading={loading}
              appliedFilters={filter}
              clearFilters={() => setFilter(initFilters)}
              changeFilters={filters => setFilter(filters)}
            />
          </div>
          {loading && (
            <div className="transactions__loading">
              <Loading />
            </div>
          )}
          {!loading && (
            <div className="transactions__table transactions__table--refunds">
              <Table
                withBtnMenu
                withoutEdgeSpacers
                cellAlign="left"
                emptyBody={<EmptyBody />}
                headers={[
                  <span className="transactions__column__left">{l('transactions.createDate')}</span>,
                  <span className="transactions__column__left">{l('refunds.transactionId')}</span>,
                  <span className="transactions__column__left">{l('refunds.id')}</span>,
                  <span className="transactions__column__left">{l('transactions.country')}</span>,
                  <span className="transactions__column__left">{l('transactions.paymentMethod')}</span>,
                  <span className="transactions__column__left transactions__title">{l('transactions.amountPaid')}</span>,
                  <span className="transactions__column__left transactions__title">{l('transactions.amountReceived')}</span>,
                  <span>{l('refunds.status')}</span>,
                ]}
                body={
                  (hasRefunds &&
                    !error &&
                    allRefunds?.data?.map(refund => {
                      return [
                        <span className="transactions__column__left">
                          <Moment format="DD/MM/YYYY">{createDate(refund.date)}</Moment>
                        </span>,
                        <span className="transactions__column__left">
                          <CopyToClipboard
                            text={refund.transactionExternalId}
                            onCopy={() => {
                              setWasCopied(true);
                            }}
                          >
                            <Tooltip
                              onMouseEnter={() => setWasCopied(false)}
                              title={l(wasCopied ? 'common.copied' : 'common.copy')}
                            >
                              <div className="transactions__column__copy">
                                <CopyIcon />
                                <span>{l('clients.copyId')}</span>
                              </div>
                            </Tooltip>
                          </CopyToClipboard>
                        </span>,
                        <span className="transactions__column__left transactions__column__invoice">
                          <span>{refund.id}</span>
                        </span>,
                        <span className="transactions__column__left transactions__country">
                          <img
                            alt=""
                            className="transactions__country__flag country-flag--rounded"
                            src={`${Urls.roundedFlags.replace(':code', getMappedCountryCode(refund.country))}`}
                          />
                          <span>{l(`countryCode.${refund.country}`)}</span>
                        </span>,
                        <div className="transactions__payment_method">
                          {refund.paymentMethodCode ? (
                            <img
                              alt={refund.paymentMethod ?? ''}
                              className="transactions__payment_method__flag"
                              src={`${Urls.paymentMethodsWide.replace(':code', refund.paymentMethodCode)}`}
                            />
                          ) : (
                            <span className="transactions__column__left">{refund.paymentMethod ?? '-'}</span>
                          )}
                        </div>,
                        <span className="transactions__column__left">
                          {refund.currency} {refund.localAmount}
                        </span>,
                        <span className="transactions__column__left">
                          {refund.balanceCurrency ?? ''} {refund.balanceAmount ?? '-'}
                        </span>,
                        <div className="transactions__status-cell">
                          {isRefundCompleted(refund.status) ? (
                            <TransactionCompletedStatusIcon tooltipTitle={l('refunds.status.COMPLETED')} />
                          ) : (
                            <StatusIcon status={refund.status} />
                          )}
                        </div>,
                        <div className="transactions__actions-cell">
                          {(refund.country || isRefundCompleted(refund.status)) && (
                            <ButtonMore
                              variant={isRefundCompleted(refund.status) ? 'detail' : 'menu'}
                              onClick={() => {
                                history.push(Routes.REFUND_DETAIL.replace(':invoice_id', String(refund.id)));
                              }}
                            />
                          )}
                        </div>,
                      ];
                    })) ||
                  null
                }
                rowsPerPageOptions={[allRefunds?.size]}
                rowsLength={allRefunds?.totalElements}
                rowsPerPage={allRefunds?.size}
                page={allRefunds?.page}
                handleChangePage={(event, newPage) => {
                  getRefunds(newPage, filter);
                }}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Refunds;
